import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { HiArrowRight, HiArrowLeft, HiCheck, HiLink } from "react-icons/hi";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateSingle } from "../../hooks/useTranslateSingle";

// ─── Teks UI statis ────────────────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    breadHome:    "Home",
    breadProgram: "Program",
    partnerLabel: "Program Partner",
    aboutLabel:   "About Program",
    shareBtn:     "Share Program",
    backBtn:      "Back to Program",
    caption:      "Rumoh Pangan Aceh Program",
    loading:      "Loading program...",
    error:        "Failed to load program.",
    copiedMsg:    "Link copied to clipboard!",
    copiedSub:    "Share this program with others",
  },
  ID: {
    breadHome:    "Beranda",
    breadProgram: "Program",
    partnerLabel: "Mitra Program",
    aboutLabel:   "Tentang Program",
    shareBtn:     "Bagikan Program",
    backBtn:      "Kembali ke Program",
    caption:      "Program Rumoh Pangan Aceh",
    loading:      "Memuat program...",
    error:        "Gagal memuat program.",
    copiedMsg:    "Tautan berhasil disalin!",
    copiedSub:    "Bagikan program ini ke orang lain",
  },
};

// ─── Hero ──────────────────────────────────────────────────────────────────────
const Hero = ({ program }) => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
 
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);
 
  if (!program) return null;
 
  const hasImage = program.url && program.url !== null;
 
  return (
    <section className="bg-white pt-10 pb-0 px-4">
      <div className="max-w-4xl mx-auto">
 
        {/* Breadcrumb */}
        <div
          className="flex items-center gap-2 mb-8 text-xs text-[#a09880] font-medium tracking-wide"
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.6s ease 0.1s" }}
        >
          <a href="/" className="hover:text-[#c9862a] transition-colors">{tx.breadHome}</a>
          <span>/</span>
          <a href="/program" className="hover:text-[#c9862a] transition-colors">{tx.breadProgram}</a>
          <span>/</span>
          <span className="text-[#c9862a]">{program.judul}</span>
        </div>
 
        {/* Hero Card */}
        <div
          className="w-full relative overflow-hidden bg-[#f7f3ec] border border-[#e8e0d0]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.9s ease 0.4s",
          }}
        >
          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center select-none pointer-events-none overflow-hidden">
            <span className="font-serif text-[160px] md:text-[220px] font-bold text-[#1a4a35]/[0.04] leading-none whitespace-nowrap">
              PROGRAM
            </span>
          </div>
 
          {/* Top accent */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9862a] via-[#e09b3a] to-transparent" />
 
          <div className="relative flex flex-col items-center justify-center py-16 px-8 gap-6">
 
            {/* ── Logo: hanya tampil jika ada URL ── */}
            {hasImage && (
              <div className="relative group">
                <div className="absolute -inset-4 border border-[#c9862a]/20 rounded-full group-hover:border-[#c9862a]/50 transition-all duration-700" />
                <div className="absolute -inset-8 border border-[#c9862a]/10 rounded-full" />
                <div className="w-40 h-40 md:w-52 md:h-52 rounded-full overflow-hidden border-4 border-white shadow-xl shadow-[#1a1a18]/10 relative">
                  <img
                    src={program.url}
                    alt={program.judul}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            )}
 
            {/* Label + Judul */}
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex items-center gap-3">
                <div className="h-px w-8 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                  {tx.partnerLabel}
                </span>
                <div className="h-px w-8 bg-[#c9862a]" />
              </div>
              <span className="font-serif text-2xl md:text-3xl font-bold text-[#1a4a35]">
                {program.judul}
              </span>
            </div>
          </div>
 
          {/* Caption */}
          <div className="absolute bottom-0 right-0 bg-[#1a1a18]/70 px-4 py-2">
            <span className="text-white/60 text-xs tracking-wide">{tx.caption}</span>
          </div>
 
          {/* Bottom accent */}
          <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#c9862a] to-transparent" />
        </div>
      </div>
    </section>
  );
};

// ─── Deskripsi Section ─────────────────────────────────────────────────────────
const DeskripsiSection = ({ program }) => {
  const [showDescription, setShowDescription] = useState(false);
  const [copied, setCopied] = useState(false);

  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDescription(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-hide toast setelah beberapa detik
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback untuk browser lama / non-HTTPS
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  if (!program) return null;

  const paragraphs = program.deskripsi
    ? program.deskripsi.split("\n\n").filter(Boolean)
    : [];

  if (!showDescription) {
    return (
      <section className="bg-white py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#a09880] text-sm animate-pulse">
            Memuat deskripsi...
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-14 px-4 relative">
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-12 relative">

          {/* Left amber line */}
          <aside className="hidden lg:flex flex-col w-10 flex-shrink-0 pt-1">
            <div
              className="w-[2px] bg-gradient-to-b from-[#c9862a] to-transparent mx-auto"
              style={{ height: "100%", transition: "height 1.8s ease", minHeight: 320 }}
            />
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* Section label */}
            <div style={{ opacity: 1, transform: "translateY(0)", transition: "all 0.6s ease 0.1s" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-10 bg-[#c9862a]" />
                <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.aboutLabel}</span>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-10">
              <div className="flex-1 h-px bg-[#e8e0d0]" />
              <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
              <div className="flex-1 h-px bg-[#e8e0d0]" />
            </div>

            {/* deskripsi */}
            <div style={{ opacity: 1, transform: "translateY(0)", transition: "all 0.8s ease 0.25s" }}>
              {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => (
                  <p key={i} className="text-[#4a4a44] text-[15px] leading-[1.9] mb-5 text-justify">
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-[#4a4a44] text-[15px] leading-[1.9] text-justify">
                  {program.deskripsi}
                </p>
              )}
            </div>

            {/* Share + Back */}
            <div
              className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-14 pt-10 border-t border-[#e8e0d0]"
              style={{ opacity: 1, transition: "opacity 0.8s ease 0.5s" }}
            >
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-[#c9862a] text-sm font-bold tracking-wide border-b border-[#c9862a]/30 pb-0.5 hover:border-[#c9862a] transition-colors whitespace-nowrap group"
              >
                {tx.shareBtn}
                <HiArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            <div className="mt-10">
              <a
              
                href="/program"
                className="flex items-center gap-3 text-[#3a3a34] hover:text-[#c9862a] transition-colors duration-200 group w-fit"
              >
                <div className="w-10 h-10 border border-[#e8e0d0] group-hover:border-[#c9862a] flex items-center justify-center transition-colors duration-200">
                  <HiArrowLeft size={16} />
                </div>
                <span className="text-sm font-bold tracking-wide uppercase">{tx.backBtn}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Toast Notification ── */}
      <div
        className="fixed bottom-5 inset-x-0 z-50 pointer-events-none px-4 flex justify-center"
        style={{
          transform: copied ? "translateY(0)" : "translateY(16px)",
          opacity: copied ? 1 : 0,
          transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div className="flex items-center gap-3 bg-[#1a1a18] text-white pl-3 pr-4 py-3 rounded-2xl shadow-xl shadow-black/30 border border-white/10 pointer-events-auto max-w-xs w-full sm:w-auto">
          <div className="w-8 h-8 rounded-full bg-[#c9862a] flex items-center justify-center flex-shrink-0">
            <HiCheck size={15} className="text-white" />
          </div>
          <span className="text-[13px] font-semibold leading-tight">{tx.copiedMsg}</span>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const ViewProgram = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];
  const { slug } = useParams();

const [rawProgram, setRawProgram] = useState(null);
const [loading, setLoading]       = useState(true);
const [error, setError]           = useState(null);

useEffect(() => {
  const fetchProgram = async () => {
    try {
      const apiUrl = process.env.REACT_APP_URL_API;
      const res = await axios.get(`${apiUrl}/view-program/slug/${slug}`);
      setRawProgram(res.data);
    } catch (err) {
      console.error("Error fetching program:", err);
      setError(tx.error);
    } finally {
      setLoading(false);
    }
  };
  if (slug) fetchProgram();
}, [slug]);

  // 2. Terjemahkan otomatis saat language === "EN"
  //    Tidak ada HTML Quill → semua masuk fields (bukan htmlFields)
  const { translated: program, loading: translating } = useTranslateSingle(
    rawProgram,
    language,
    ["judul", "deskripsi"],  // ← plain text biasa, tidak perlu strip HTML
    []                       // ← htmlFields kosong
  );

  if (loading || translating)
    return (
      <main className="flex items-center justify-center min-h-screen font-homepage">
        <span className="text-[#a09880] text-sm animate-pulse">{tx.loading}</span>
      </main>
    );

  if (error)
    return (
      <main className="flex items-center justify-center min-h-screen font-homepage">
        <span className="text-red-500 text-sm">{error}</span>
      </main>
    );

  return (
    <main className="overflow-x-hidden font-homepage bg-white">
      <Navbar />
      <section id="hero"><Hero program={program} /></section>
      <section id="deskripsi"><DeskripsiSection program={program} /></section>
      <section id="footer"><Footer /></section>
    </main>
  );
};

export default ViewProgram;